import React, { useState, useEffect } from 'react';
import { CustomTextAreaWithCounter, FormError } from 'components';
import { connect } from 'react-redux';
import { CFCProps } from 'types';
import { RootState } from 'redux/root.reducer';
import {
  Button,
  FileUploader,
  FileUploaderItem,
  InlineLoading
} from 'carbon-components-react';
import _ from 'lodash';

const SafetyMessageAndLogoForm: React.FC<Props | any> = (props: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDataUpdated, setIsDataUpdated] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);

  const {
    app,
    safetyMessageAndLogoCache,
    values,
    touched: fTouched,
    errors: fErrors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue
  } = props;

  // eslint-disable-next-line react/display-name
  const inputError = ((errors: any, touched: any) => (
    key: 'safetyMessage' | 'logo'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );
  useEffect(() => {
    setIsLoading(app?.loading);
    setIsSubmitting(safetyMessageAndLogoCache?.submitting);
    setIsDataUpdated(safetyMessageAndLogoCache?.safetyMessageLogoUpdated);
  }, [app, safetyMessageAndLogoCache]);

  useEffect(() => {
    if (isDataUpdated) {
      const isUpdated = setTimeout(() => {
        setIsDataUpdated(false);
      }, 1000);
      setIsSaved(true);
      return () => {
        clearTimeout(isUpdated);
      };
    }
  }, [isDataUpdated]);

  useEffect(() => {
    if (fErrors.logo) {
      const fileContainer = document.querySelector<HTMLElement>(
        '.safety-message-and-logo-form__button .bx--file-container'
      );
      if (fileContainer) {
        fileContainer.style.display = 'none';
      }
    }
  }, [fErrors.logo]);

  const isSaveEnabled = !fErrors.safetyMessage && !fErrors.logo;

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSaved(false);
    const files = event.target.files;
    const fileContainer = document.querySelector<HTMLElement>(
      '.safety-message-and-logo-form__button .bx--file-container'
    );
    if (files) {
      setFieldValue('logo', files[0]);
      if (fileContainer && fileContainer.style.display === 'none') {
        fileContainer.style.display = 'block';
      }
    }
  };

  const onDelete = (logo: any, type: string) => {
    setFieldValue('logo', {});
    if (type !== 'new') {
      setFieldValue('isDelete', true);
    }
  };

  return (
    <section className="safety-message-and-logo-form">
      <form onSubmit={handleSubmit}>
        <div className="safety-message-and-logo-form__container">
          <section className="safety-message-and-logo-form__section">
            <h6 className="safety-message-and-logo-form__header">
              Safety message
            </h6>
            <p className="safety-message-and-logo-form__description">
              {`Provide your organisation's safety message to appear on the
              enquirer's responses overview page`}
            </p>
            <section className="form-section">
              <section className="form-item">
                <CustomTextAreaWithCounter
                  id="safetyMessage"
                  labelText="Safety message"
                  rows={6}
                  helperText="Appears on the response overview page"
                  maxLength={320}
                  name="safetyMessage"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.safetyMessage}
                />
                {inputError('safetyMessage')}
              </section>
            </section>

            <hr className="section--divider section--middle" />
            <h6 className="safety-message-and-logo-form__header">
              Logo{' '}
              <span className="safety-message-and-logo-form__label-optional">
                (optional)
              </span>
            </h6>
            <p className="safety-message-and-logo-form__description">{`Your logo is displayed at the top of your organisation's response page for the enquirer`}</p>
            <section className="form-section">
              <section className="form-item">
                <h6 className="safety-message-and-logo-form__header--upload">
                  Upload file
                </h6>
                <p className="safety-message-and-logo-form__paragraph safety-message-and-logo-form__paragraph--pb">
                  Crop your logo file so that there’s no white or transparent
                  space around the image area.
                </p>
                <p className="safety-message-and-logo-form__paragraph">
                  Max size is 1MB, PNG or JPG
                </p>
                <div className="safety-message-and-logo-form__button">
                  <FileUploader
                    id="logo"
                    buttonLabel="Add file"
                    buttonKind="secondary"
                    filenameStatus="edit"
                    accept={['.jpg', '.png', '.jpeg']}
                    iconDescription="Delete file"
                    onChange={handleFileUpload}
                    onDelete={() => onDelete(values.logo, 'new')}
                    name="logo"
                    size="field"
                    onBlur={handleBlur}
                  />
                </div>
                {!_.isEmpty(values.logo) && !(values.logo instanceof File) && (
                  <div className="safety-message-and-logo-form__item">
                    <FileUploaderItem
                      id="uploaderItem"
                      iconDescription="Delete file"
                      name={values.logo?.name}
                      onDelete={() => onDelete(values.logo, '')}
                      status="edit"
                    />
                  </div>
                )}
                {values.logo && values.logo instanceof File && isSaved && (
                  <div className="safety-message-and-logo-form__item">
                    <FileUploaderItem
                      id="uploaderItemNew"
                      iconDescription="Delete file"
                      name={values.logo?.name}
                      onDelete={() => onDelete(values.logo, 'new')}
                      status="edit"
                    />
                  </div>
                )}
                <div className="error--padding">{inputError('logo')}</div>
              </section>
            </section>
          </section>
        </div>

        <hr className="section--divider" />
        <section className="safety-message-and-logo-form__section">
          {!isSubmitting && !isDataUpdated ? (
            <Button
              size="field"
              className="safety-message-and-logo-submit"
              type="submit"
              kind={!fErrors ? 'danger' : 'primary'}
              data-testid="submit"
              disabled={isLoading || isSubmitting || !isSaveEnabled}
            >
              Save
            </Button>
          ) : (
            <div className="safety-message-and-logo-submit safety-message-and-logo-submit--loader">
              <InlineLoading
                description={
                  !isSubmitting && isDataUpdated ? 'Saved' : 'Saving ...'
                }
                status={!isSubmitting && isDataUpdated ? 'finished' : 'active'}
                aria-live={'assertive'}
              />
            </div>
          )}
        </section>
      </form>
    </section>
  );
};

const mapStateToProps = ({ app, safetyMessageAndLogoCache }: RootState) => ({
  app,
  safetyMessageAndLogoCache
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

SafetyMessageAndLogoForm.displayName = 'SafetyMessageAndLogoForm';

export default connector(SafetyMessageAndLogoForm);
